import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import ProcessedOrderPrintDocument from "./ProcessedOrderPrintDocument";
import { generateAndOpenProcessedOrderPdf } from "./generateProcessedOrderPdf";
import { preparePdfQrDataUrl } from "./preparePdfQrDataUrl";
import { mapProcessedOrderApiToViewModel } from "./processedOrderViewModel";
import { sanitizeProcessedOrderInfoHtml } from "./sanitizeProcessedOrderInfoHtml";
import { getProcessedOrderByShopifyAuthUrl } from "app/services/api/processedOrder";
import "./processedOrderPrint.css";

function extractErrorMessage(err) {
  const fromApi = err?.response?.data;
  if (fromApi && typeof fromApi === "object") {
    const m = fromApi.message ?? fromApi.error ?? fromApi.msg;
    if (typeof m === "string" && m.trim()) return m.trim();
  }
  if (typeof err?.message === "string" && err.message.trim()) {
    return err.message.trim();
  }
  return "Nepodarilo sa načítať údaje objednávky.";
}

/**
 * Public processed-order page: loads order by Shopify authenticate URL query `s`.
 * GET /api/v1/processed-order/by-shopify-auth-url?s=...
 */
const MISSING_S_MSG =
  "Chýba platný odkaz na objednávku. Skontrolujte, či má URL parameter „s“.";

const DsokProcessedOrderPage = () => {
  const [searchParams] = useSearchParams();
  const s = searchParams.get("s");
  const shopifyUrl = React.useMemo(
    () => (s != null ? String(s).trim() : ""),
    [s],
  );

  const [loadState, setLoadState] = React.useState(() =>
    shopifyUrl ? "loading" : "error",
  );
  const [viewModel, setViewModel] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState(() =>
    shopifyUrl ? "" : MISSING_S_MSG,
  );
  /** PNG data URL for QR in print/PDF (client-generated or rasterized); set when preparation finishes. */
  const [pdfQrDataUrl, setPdfQrDataUrl] = React.useState(null);
  /** When true, `pdfQrDataUrl` is final for this viewModel and PDF generation may run. */
  const [pdfQrPreparationDone, setPdfQrPreparationDone] = React.useState(false);

  React.useEffect(() => {
    if (!shopifyUrl) {
      console.warn(
        "[dsok/processed-order] Missing query parameter `s` (Shopify authenticate URL).",
      );
      setLoadState("error");
      setErrorMessage(MISSING_S_MSG);
      setViewModel(null);
      return undefined;
    }

    let cancelled = false;
    setLoadState("loading");
    setErrorMessage("");
    setViewModel(null);

    getProcessedOrderByShopifyAuthUrl(shopifyUrl)
      .then((response) => {
        if (cancelled) return;
        console.log(response);
        const vm = mapProcessedOrderApiToViewModel(response);
        if (!vm) {
          setLoadState("error");
          setErrorMessage("Neplatná odpoveď servera.");
          return;
        }
        setViewModel(vm);
        setLoadState("success");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadState("error");
        setErrorMessage(extractErrorMessage(err));
      });

    return () => {
      cancelled = true;
    };
  }, [shopifyUrl]);

  React.useEffect(() => {
    if (!viewModel) {
      setPdfQrDataUrl(null);
      setPdfQrPreparationDone(false);
      return;
    }
    let cancelled = false;
    setPdfQrPreparationDone(false);
    preparePdfQrDataUrl({
      qrCodeValue: viewModel.qrCodeValue,
      qrCodeImageSrc: viewModel.qrCodeImageSrc,
    })
      .then((url) => {
        if (!cancelled) {
          setPdfQrDataUrl(url);
          setPdfQrPreparationDone(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPdfQrDataUrl(null);
          setPdfQrPreparationDone(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [viewModel]);

  const printRootRef = React.useRef(null);
  const pdfBusyRef = React.useRef(false);
  /** Increments per auto-PDF effect run so stale async work can bail (Strict Mode, URL changes). */
  const pdfAutoEffectGenerationRef = React.useRef(0);

  const [pdfGenerating, setPdfGenerating] = React.useState(false);
  const [pdfAutoOpenBlocked, setPdfAutoOpenBlocked] = React.useState(false);
  const [pdfActionError, setPdfActionError] = React.useState("");

  const getPrintRootElement = React.useCallback(() => {
    return (
      printRootRef.current ??
      (typeof document !== "undefined"
        ? document.querySelector(".processed-order-print-root")
        : null)
    );
  }, []);

  const runOpenPdf = React.useCallback(
    async ({ isAuto, shouldAbort, preOpenedWindow } = {}) => {
      if (pdfBusyRef.current) return;
      if (!pdfQrPreparationDone) return;

      const el = getPrintRootElement();
      if (!el) {
        if (!isAuto) {
          setPdfActionError(
            "Nepodarilo sa nájsť obsah dokumentu na export. Obnovte stránku a skúste znova.",
          );
        }
        try { preOpenedWindow?.close(); } catch (_) {}
        return;
      }

      pdfBusyRef.current = true;
      setPdfGenerating(true);
      setPdfActionError("");
      if (isAuto) {
        setPdfAutoOpenBlocked(false);
      }
      try {
        const result = await generateAndOpenProcessedOrderPdf(el, {
          shouldAbort,
          preOpenedWindow,
        });
        if (result.aborted) return;
        if (result.blocked && isAuto) {
          setPdfAutoOpenBlocked(true);
        } else if (result.opened && isAuto) {
          setPdfAutoOpenBlocked(false);
        }
      } catch (err) {
        console.error("[dsok/processed-order] PDF generation failed", err);
        const msg =
          typeof err?.message === "string" && err.message.trim()
            ? err.message.trim()
            : "Nepodarilo sa vytvoriť PDF.";
        if (!isAuto) {
          setPdfActionError(msg);
        }
        if (isAuto) {
          setPdfAutoOpenBlocked(true);
        }
      } finally {
        pdfBusyRef.current = false;
        setPdfGenerating(false);
      }
    },
    [getPrintRootElement, pdfQrPreparationDone],
  );

  const handleOpenPdf = React.useCallback(() => {
    setPdfAutoOpenBlocked(false);
    setPdfActionError("");
    // Open the window NOW — synchronously, while still inside the user gesture.
    // iOS Safari blocks window.open() called after any await, so we must do it here.
    const preOpenedWindow =
      typeof window !== "undefined"
        ? window.open("about:blank", "_blank", "noopener,noreferrer")
        : null;
    void runOpenPdf({ isAuto: false, preOpenedWindow });
  }, [runOpenPdf]);

  const showContent = loadState === "success" && viewModel != null;

  React.useEffect(() => {
    if (!showContent || !shopifyUrl || !pdfQrPreparationDone) return;

    const generation = ++pdfAutoEffectGenerationRef.current;
    let cancelled = false;

    const tryRun = (frame) => {
      if (cancelled || generation !== pdfAutoEffectGenerationRef.current) return;
      const el = getPrintRootElement();
      if (el) {
        void runOpenPdf({
          isAuto: true,
          shouldAbort: () =>
            cancelled || generation !== pdfAutoEffectGenerationRef.current,
        });
        return;
      }
      if (frame > 60) return;
      requestAnimationFrame(() => tryRun(frame + 1));
    };

    const t = window.setTimeout(() => {
      requestAnimationFrame(() => tryRun(0));
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [
    showContent,
    shopifyUrl,
    pdfQrPreparationDone,
    runOpenPdf,
    getPrintRootElement,
  ]);

  const thankYouDescriptionHtml = showContent
    ? sanitizeProcessedOrderInfoHtml(viewModel.thankYouDescription)
    : "";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f5f7fb",
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
        {loadState === "loading" ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            py={8}
            px={2}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Načítavam objednávku…
            </Typography>
          </Box>
        ) : null}

        {loadState === "error" ? (
          <Container
            maxWidth="sm"
            sx={{
              py: { xs: 3, sm: 4 },
              px: { xs: 2, sm: 3 },
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Alert
              severity="error"
              sx={{
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {errorMessage}
            </Alert>
          </Container>
        ) : null}

        {showContent ? (
          <>
            <Box
              className="processed-order-screen-only"
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2, sm: 4 },
                boxSizing: "border-box",
              }}
            >
              <Container
                maxWidth="sm"
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 600 },
                  px: { xs: 2, sm: 3 },
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    textAlign: "center",
                    bgcolor: "background.paper",
                    borderRadius: { xs: 2, sm: 3 },
                    p: { xs: 2.5, sm: 4, md: 5 },
                    boxShadow: "0 8px 32px rgba(21, 40, 60, 0.08)",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                    boxSizing: "border-box",
                  }}
                >
                  {viewModel.screenLogoUrl ? (
                    <Box
                      component="img"
                      src={viewModel.screenLogoUrl}
                      alt=""
                      sx={{
                        maxWidth: { xs: 200, sm: 220 },
                        width: "100%",
                        height: "auto",
                        mx: "auto",
                        mb: { xs: 2, sm: 3 },
                        display: "block",
                        objectFit: "contain",
                      }}
                    />
                  ) : null}

                  {viewModel.thankYouTitle ? (
                    <Typography
                      variant="h4"
                      component="h1"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        fontSize: { xs: "1.35rem", sm: "1.6rem", md: "1.75rem" },
                        lineHeight: 1.3,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        px: { xs: 0, sm: 0.5 },
                      }}
                    >
                      {viewModel.thankYouTitle}
                    </Typography>
                  ) : null}

                  {pdfActionError ? (
                    <Alert
                      severity="error"
                      sx={{ mb: 2, textAlign: "left" }}
                      onClose={() => setPdfActionError("")}
                    >
                      {pdfActionError}
                    </Alert>
                  ) : null}

                  {pdfAutoOpenBlocked ? (
                    <Alert severity="info" sx={{ mb: 2, textAlign: "left" }}>
                      PDF sa neotvoril automaticky (napr. blokovanie vyskakovacích okien).
                      Použite tlačidlo nižšie na otvorenie dokumentu.
                    </Alert>
                  ) : null}

                  {thankYouDescriptionHtml ? (
                    <Box
                      className="processed-order-screen-thank-html"
                      component="div"
                      sx={{
                        mb: { xs: 3, sm: 4 },
                        lineHeight: 1.65,
                        width: "100%",
                        maxWidth: { xs: "100%", sm: 440 },
                        minWidth: 0,
                        mx: "auto",
                        color: "text.secondary",
                        textAlign: "left",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        "& > *": { mb: 1 },
                        "& > *:last-child": { mb: 0 },
                        "& p": { mb: 1 },
                        "& p:last-child": { mb: 0 },
                        "& ul, & ol": {
                          pl: { xs: 2, sm: 2.5 },
                          mb: 1,
                          my: 0,
                          maxWidth: "100%",
                          "& li": { mb: 0.5 },
                          "& li:last-child": { mb: 0 },
                        },
                        "& img": {
                          maxWidth: "100%",
                          height: "auto",
                        },
                        "& table": {
                          display: "block",
                          width: "100%",
                          maxWidth: "100%",
                          overflowX: "auto",
                        },
                        "& a": {
                          color: "primary.main",
                          wordBreak: "break-word",
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: thankYouDescriptionHtml,
                      }}
                    />
                  ) : null}

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={pdfGenerating || !pdfQrPreparationDone}
                    onClick={handleOpenPdf}
                    startIcon={
                      pdfGenerating || !pdfQrPreparationDone ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : null
                    }
                    sx={{
                      py: { xs: 1.75, sm: 2 },
                      minHeight: 48,
                      fontSize: { xs: "1rem", sm: "1.05rem" },
                      fontWeight: 600,
                      width: "100%",
                      maxWidth: { xs: "100%", sm: 420 },
                      mx: "auto",
                      display: "flex",
                      borderRadius: 2,
                      textTransform: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    {pdfGenerating
                      ? "Pripravujem PDF…"
                      : !pdfQrPreparationDone
                        ? "Pripravujem QR…"
                        : viewModel.printButtonLabel}
                  </Button>
                </Box>
              </Container>
            </Box>

            <ProcessedOrderPrintDocument
              ref={printRootRef}
              viewModel={viewModel}
              pdfQrDataUrl={pdfQrDataUrl}
              pdfQrPreparationDone={pdfQrPreparationDone}
            />
          </>
        ) : null}
    </Box>
  );
};

export default DsokProcessedOrderPage;
